import {
  AddRounded,
  DeleteOutlineRounded,
  EditRounded,
  SearchRounded,
  VisibilityRounded,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CardActions,
  CardContent,
  Chip,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  TextField as MuiTextField,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import MainLayout from "../../layouts/main/main_layout";
import LandingPageLayout from "../../layouts/landing_page/landing_page_layout";
import AccessDenied from "../../components/access_denied/access_denied";
import Loading from "../../components/loading/loading";
import useUser from "../../providers/user/user_provider";
import { useFeedback } from "../../providers/feedback/feeedback";
import { queryClient } from "../../providers/query/query";
import { deleteLandingPage, getLandingPages } from "../../services/landing_page/landing_page";
import { DeleteLandingPageDialog } from "./components/delete-landing-page-dialog";
import FilterLandingPage, { LandingPageFilterValues } from "./components/filter_landing_page";
import SortLandingPagePopup, {
  DEFAULT_LANDING_PAGE_SORT_OPTION,
  LandingPageSortOption,
  landingPageSortOptionLabel,
  landingPageSortOptionToParams,
} from "./components/sort_landing_page_popup";
import {
  TableCell,
  TableEmptyState,
  TableFooterPagination,
  TableHeaderRow,
  TableRow,
} from "../users/components/table_toolkit/table_toolkit";
import { ToolbarButton } from "../../components/toolbar_button/toolbar_button";
import FilterIcon from "../../assets/filter_icon.svg";
import SortIcon from "../../assets/sort.svg";
import OrgSelector from "../../components/org_selector/org_selector";
import { useSelectedOrgStore } from "../../store/use-selected-org-store";
import { LandingPageContent } from "./model/landing_page";
import { black, greyEnabled, purple, purpleLight } from "../../theme/colors";

const ROWS_PER_PAGE_OPTIONS = [8, 25, 50, 100];

const TextField = styled(MuiTextField)(() => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    "& fieldset": { borderColor: "#ABA0AB" },
    "&:hover fieldset": { borderColor: purple },
    "&.Mui-focused fieldset": { borderColor: purple, borderWidth: 2 },
  },
}));

const EMPTY_FILTERS: LandingPageFilterValues = {
  module: null,
  fromDate: "",
  toDate: "",
};

const stripHtmlTags = (html: string) => {
  if (!html) return "";
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

function LandingPageList() {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = useUser();
  const { showSnackbar } = useFeedback();

  const currentTab = location.pathname.includes("/rules") ? 1 : 0;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);
  const [searchValue, setSearchValue] = useState("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<LandingPageContent | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState<LandingPageFilterValues>(EMPTY_FILTERS);
  const [sortOption, setSortOption] = useState<LandingPageSortOption>(DEFAULT_LANDING_PAGE_SORT_OPTION);
  const [sortDialogOpen, setSortDialogOpen] = useState(false);
  const { selectedOrgId } = useSelectedOrgStore();

  const activeFilterCount = [filters.module, filters.fromDate && filters.toDate].filter(Boolean).length;

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearchValue(searchValue), 500);
    return () => clearTimeout(handler);
  }, [searchValue]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchValue, filters, selectedOrgId, sortOption, pageSize]);

  const { data: pageData, isLoading, isError } = useQuery(
    ["landing-page-contents", page, pageSize, filters, selectedOrgId, sortOption],
    async () => {
      const { sortBy, sortOrder } = landingPageSortOptionToParams(sortOption);
      let response = await getLandingPages({
        page,
        pageSize,
        module: filters.module || undefined,
        startDate: filters.fromDate || undefined,
        endDate: filters.toDate || undefined,
        orgId: selectedOrgId,
        sortBy,
        sortOrder,
      });
      return response?.data?.data as { total: number; list: LandingPageContent[] };
    }
  );

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage + 1);
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    if (newValue === 0) navigate("/landing-page");
    else navigate("/rules");
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    deleteLandingPage(deleteTarget.id)
      .then(() => {
        showSnackbar("Content deleted successfully");
        queryClient.invalidateQueries({ queryKey: ["landing-page-contents"] });
        setDeleteTarget(null);
      })
      .catch((e) => {
        showSnackbar(
          e?.response?.data?.error?.message ||
            "Something went wrong while deleting the content."
        );
      })
      .finally(() => setIsDeleting(false));
  };

  const count = pageData?.total || 0;
  const list = pageData?.list || [];

  const startRange = (page - 1) * pageSize + 1;
  const endRange = Math.min(page * pageSize, count);
  const customText = `Showing ${count ? startRange : 0} to ${endRange || 0} of ${count || 0} entries`;
  const showList = !isLoading && !isError && list.length > 0;
  const showEmpty = !isLoading && !isError && list.length === 0;

  return (
    <MainLayout>
      <LandingPageLayout page={page} setPage={setPage}>
        <Box sx={{ borderBottom: 1, borderColor: "divider", px: { xs: 2.5, sm: 3 } }}>
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            sx={{ "& .MuiTab-root": { textTransform: "none", fontSize: "1rem", fontWeight: 500 } }}
          >
            <Tab label="Contents" />
            <Tab label="Rules" />
          </Tabs>
        </Box>

        <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
            mb={2}
          >
            <Box maxWidth={{ xs: "100%", sm: 360 }} flex={1} minWidth={{ xs: "100%", sm: 220 }}>
              <TextField
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                size="small"
                fullWidth
                placeholder="Search by content name"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRounded sx={{ color: greyEnabled, fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
              <OrgSelector lockToOwnOrg />
              <ToolbarButton
                icon={SortIcon}
                label={landingPageSortOptionLabel(sortOption)}
                onClick={() => setSortDialogOpen(true)}
              />
              <ToolbarButton
                icon={FilterIcon}
                label="Filters"
                badgeCount={activeFilterCount}
                onClick={() => setFilterDialogOpen(true)}
              />
              {hasPermission("landing-page", "create") && (
                <Button
                  onClick={() => navigate("/landing-page/add-landing-page")}
                  variant="contained"
                  disableElevation
                  startIcon={<AddRounded sx={{ height: 18, width: 18 }} />}
                  sx={{
                    bgcolor: purple,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": { bgcolor: "#7A2074" },
                  }}
                >
                  Add Content
                </Button>
              )}
            </Box>
          </Box>

          {isLoading ? (
            <Box display="flex" justifyContent="center" py={8}>
              <Loading />
            </Box>
          ) : isError ? (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              py={8}
              gap={0.5}
            >
              <Typography fontWeight={600} color={black}>
                Something went wrong
              </Typography>
              <Typography variant="body2" color={greyEnabled}>
                We couldn't load the content pages. Please try again.
              </Typography>
            </Box>
          ) : showEmpty ? (
            <TableEmptyState
              message="No content pages found"
              hint="Try adjusting your search or filters, or add a new content page."
            />
          ) : (
            <Box mt={1}>
              <TableHeaderRow
                labels={[
                  { label: "Sl. No.", flex: 0.3 },
                  { label: "Content", flex: 2 },
                  { label: "Module", flex: 0.7 },
                  { label: "Created On", flex: 0.7 },
                  { label: "Actions" },
                ]}
                lastColumnWidth={150}
                align="left"
                bordered
              />
              {list.map((item, index) => {
                const enContent = item.contents?.["content_en"];
                const description = stripHtmlTags(enContent?.description || "");
                return (
                  <TableRow key={item.id} bordered>
                    <TableCell label="Sl. No." flex={0.3} bordered>
                      <Typography fontSize={14} color={black} noWrap>
                        {(page - 1) * pageSize + index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell label="Content" flex={2} bordered>
                      <Typography fontWeight={600} color={black} noWrap>
                        {item.content_name}
                      </Typography>
                      <Typography variant="body2" color={greyEnabled} noWrap>
                        {enContent?.title ? `${enContent.title} · ` : ""}
                        {description.length > 80 ? `${description.substring(0, 80)}...` : description || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell label="Module" flex={0.7} bordered>
                      {item.module ? (
                        <Chip
                          label={item.module}
                          size="small"
                          sx={{ bgcolor: purpleLight, color: purple, fontWeight: 600, height: 20 }}
                        />
                      ) : (
                        <Typography fontSize={14} color={greyEnabled}>-</Typography>
                      )}
                    </TableCell>
                    <TableCell label="Created On" flex={0.7} bordered>
                      <Typography fontSize={14} color={black} noWrap>
                        {item.createdAt?.split("T")[0] || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell isLast lastColumnWidth={150} bordered>
                      <Box display="flex" justifyContent={{ xs: "flex-start", sm: "flex-end" }} gap={0.5}>
                        <Tooltip title="View details">
                          <IconButton
                            onClick={() =>
                              navigate(`/landing-page/view-landing-page/${item.id}`, {
                                state: { landingPageData: item },
                              })
                            }
                            sx={{ bgcolor: "#E7F5EC", "&:hover": { bgcolor: "#D6EEE0" } }}
                          >
                            <VisibilityRounded sx={{ color: "#1E6446", fontSize: 20 }} />
                          </IconButton>
                        </Tooltip>
                        {hasPermission("landing-page", "update") && (
                          <Tooltip title="Edit content">
                            <IconButton
                              onClick={() =>
                                navigate(`/landing-page/edit-landing-page/${item.id}`, {
                                  state: { landingPageData: item },
                                })
                              }
                              sx={{ bgcolor: purpleLight, "&:hover": { bgcolor: "#F0D9F0" } }}
                            >
                              <EditRounded sx={{ color: purple, fontSize: 20 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {hasPermission("landing-page", "delete") && (
                          <Tooltip title="Delete content">
                            <IconButton
                              onClick={() => setDeleteTarget(item)}
                              sx={{ bgcolor: "#FBE7E4", "&:hover": { bgcolor: "#FBD9D6" } }}
                            >
                              <DeleteOutlineRounded sx={{ color: "#C0392B", fontSize: 20 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </Box>
          )}
        </CardContent>

        {showList && (
          <CardActions sx={{ px: 4, pb: 3 }}>
            <Box width="100%">
              <TableFooterPagination
                count={count}
                page={page - 1}
                onPageChange={handleChangePage}
                summaryText={customText}
                rowsPerPage={pageSize}
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                onRowsPerPageChange={setPageSize}
              />
            </Box>
          </CardActions>
        )}
      </LandingPageLayout>

      <DeleteLandingPageDialog
        open={!!deleteTarget}
        contentName={deleteTarget?.content_name}
        onClose={() => !isDeleting && setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
      />

      <SortLandingPagePopup
        open={sortDialogOpen}
        onClose={() => setSortDialogOpen(false)}
        sortOption={sortOption}
        setSortOption={setSortOption}
      />

      {filterDialogOpen && (
        <FilterLandingPage
          open={filterDialogOpen}
          onClose={() => setFilterDialogOpen(false)}
          module={filters.module}
          fromDate={filters.fromDate}
          toDate={filters.toDate}
          onApply={setFilters}
        />
      )}
    </MainLayout>
  );
}

export default function LandingPage() {
  const { hasPermission } = useUser();

  return hasPermission("landing-page", "read") ? (
    <LandingPageList />
  ) : (
    <MainLayout>
      <Box display="flex" flex={1} width="100%" height="100%">
        <AccessDenied />
      </Box>
    </MainLayout>
  );
}
